import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  gridFilteredSortedRowIdsSelector,
  GridHeaderCheckbox,
  GridToolbarDensitySelector,
  GridToolbarExport,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { Button, Theme } from "@mui/material";
import Grid from "@mui/material/Grid";
import React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { SettingsBackupRestore } from "@mui/icons-material";
import { telemetryProps } from "../../../instrumentation/gtm";

const MuiDataGridToolbarStyles = (theme: Theme) => {
  const tableBorder = theme.palette.table?.border ?? theme.palette.divider;

  return {
    toolbar: {
      position: "relative",
      width: "100%",
      borderBottom: "0.5px solid" + tableBorder,
      color: theme.palette.text.secondary,
    },
    highlight:
      theme.palette.mode === "light"
        ? {
            color: theme.palette.text.secondary,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: alpha(theme.palette.secondary.main, 0.2),
          },
  };
};
export default function MuiDataGridToolBar({
  customToolbar,
  checkboxSelection,
  hideToolbarCheckbox,
  saveUserConfig,
  treeData,
  handleResetTableView,
  isDefaultView,
  viewSelect,
  rowDensityMenu,
  exportMenu,
}: Readonly<{
  customToolbar?: () => React.ReactElement<any>;
  checkboxSelection: boolean | undefined;
  hideToolbarCheckbox?: boolean;
  saveUserConfig: boolean;
  treeData: boolean | undefined;
  handleResetTableView: () => void;
  isDefaultView: boolean;
  viewSelect?: () => React.ReactElement<any>;
  rowDensityMenu: boolean;
  exportMenu: boolean;
}>) {
  const theme = useTheme();
  const styles = MuiDataGridToolbarStyles(theme);
  const toolbarApiRef = useGridApiContext();
  const selectedRows = toolbarApiRef.current.getSelectedRows();

  const treeDataHeaderCheckbox = () => {
    let data: {
      indeterminate?: boolean;
      checked?: boolean;
      onClick?: (e: MouseEvent) => void;
    } = {};
    if (treeData) {
      const children = gridFilteredSortedRowIdsSelector(toolbarApiRef).filter(
        (id) => !id.toString().includes("auto-generated"),
      );

      // v8: selectedIdsLookupSelector was removed; derive selection from the
      // Map returned by getSelectedRows() (keyed by row id).
      const indeterminate =
        children?.some((child) => !selectedRows.has(child)) &&
        children?.some((child) => selectedRows.has(child));

      const checked =
        toolbarApiRef.current.getAllRowIds().length !== 0 &&
        children?.every((child) => selectedRows.has(child));
      data = {
        onClick: (e) => {
          toolbarApiRef.current.selectRows(children, !checked);
          e.preventDefault();
        },
        indeterminate,
        checked,
      };

      return data;
    }
  };

  if (!customToolbar && !saveUserConfig) return <></>;
  return (
    <Grid
      container
      flexWrap={"nowrap"}
      justifyContent={"space-between"}
      alignItems={"flex-start"}
      sx={[
        styles.toolbar,
        selectedRows.size > 0 && !hideToolbarCheckbox && styles.highlight,
      ]}
    >
      <Grid
        container
        size="auto"
        alignItems={"center"}
        sx={{ padding: 0.7, paddingRight: 0 }}
      >
        {checkboxSelection && !hideToolbarCheckbox && (
          <GridHeaderCheckbox
            field={GRID_CHECKBOX_SELECTION_COL_DEF.field}
            colDef={{ ...GRID_CHECKBOX_SELECTION_COL_DEF, computedWidth: 100 }}
            {...treeDataHeaderCheckbox()}
          />
        )}
        {viewSelect?.()}
      </Grid>
      <Grid
        container
        size="grow"
        flexGrow={1}
        justifyContent={"flex-end"}
        flexWrap={"nowrap"}
        sx={{ padding: 1, paddingLeft: 0 }}
      >
        {customToolbar?.()}
        {rowDensityMenu && (
          <GridToolbarDensitySelector
            slotProps={{
              button: {
                startIcon: null,
                style: { minWidth: 40, color: "inherit" },
              },
            }}
          />
        )}
        {exportMenu && (
          <GridToolbarExport
            slotProps={{
              button: {
                startIcon: null,
                style: { minWidth: 40, color: "inherit" },
              },
            }}
          />
        )}
        {saveUserConfig && !isDefaultView && (
          <Button
            color="inherit"
            name="Restore to Default"
            size="small"
            onClick={handleResetTableView}
            {...telemetryProps({ name: "grid_reset_view", section: "grid_toolbar", label: "Reset to Default" })}
            startIcon={<SettingsBackupRestore />}
            sx={{ whiteSpace: "nowrap", px: 3, alignSelf: "start", height: 34 }}
          >
            Reset to Default
          </Button>
        )}
      </Grid>
    </Grid>
  );
}
